import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
} from "@mui/material";
import Swal from "sweetalert2";
import axios from "axios";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { fetchOrganizations } from "../../../Apis/Organization-apis";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { clearClientPreferredOrganizationId } from "../../../Utils/clientActiveOrganization";
import {
  getAttendanceLoginOrganizations,
  assertManagerAttendanceLoginAllowed,
  buildManagerKioskStateForUser,
  persistAttendanceLandingState,
  clearAttendanceLandingState,
  isInternAttendanceUser,
  navigateAfterAttendanceLogin,
  canAccessAttendanceLogin,
  shouldUseManagerKioskFlow,
  isAdminAttendanceUser,
  isManagerAttendanceOrganization,
  shouldShowManagerAttendanceOrganizations,
  mergeOrganizationAttendanceSettings,
} from "../../../Utils/attendanceAuth";
import { activateOrganizationAndHydrate } from "../../../Utils/attendanceSession";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronRight, LogOutIcon } from "lucide-react";

const getOrganizationInitials = (name = "") => {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "OR";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
};

const OrganizationChangePage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switchingOrgId, setSwitchingOrgId] = useState(null);
  const { userData, login, logout, setMode } = useAuthStore();
  const navigate = useNavigate();

  const handleOrganizationClick = async (item) => {
    if (!userData?.application_user_id) return;

    setSwitchingOrgId(item.organization_id);
    try {
      const sessionUser = await activateOrganizationAndHydrate(
        item.organization_id,
        userData.application_user_id
      );

      login(sessionUser);
      clearAttendanceLandingState();

      if (shouldUseManagerKioskFlow(sessionUser)) {
        const managerDenial = assertManagerAttendanceLoginAllowed(sessionUser);
        if (managerDenial) {
          toast.error(managerDenial);
          return;
        }

        const state = buildManagerKioskStateForUser(sessionUser, item);
        persistAttendanceLandingState(state);
        navigate("/", { state, replace: true });
        return;
      }

      if (!canAccessAttendanceLogin(sessionUser)) {
        toast.error(
          isInternAttendanceUser(sessionUser)
            ? "Intern record not found for this organization. Contact your HR admin."
            : "Employee record not found for this organization. Contact your HR admin."
        );
        return;
      }

      const navigated = navigateAfterAttendanceLogin(
        sessionUser,
        navigate,
        setMode
      );
      if (!navigated) {
        toast.error("Could not open attendance for this organization.");
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        navigate("/login", { replace: true });
      } else {
        console.error("Failed to switch organization:", error);
        toast.error(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to update organization"
        );
      }
    } finally {
      setSwitchingOrgId(null);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.get(`${MAIN_URL}/api/auth/logout`, {
            headers: getApiHeaders(),
          });

          logout();
          clearClientPreferredOrganizationId();
          clearAttendanceLandingState();
          localStorage.removeItem("token");
          Swal.fire("Logged out!", "You have been logged out.", "success");
          navigate("/login");
        } catch (error) {
          console.error("Logout failed:", error);
          logout();
          clearClientPreferredOrganizationId();
          clearAttendanceLandingState();
          localStorage.removeItem("token");
          navigate("/login");
        }
      }
    });
  };

  useEffect(() => {
    let isMounted = true;

    const scopedOrgs = getAttendanceLoginOrganizations(userData);
    const clientId =
      userData?.client_id ||
      scopedOrgs[0]?.client_id ||
      userData?.organization?.client_id;

    const shouldFetchAllClientOrgs =
      shouldUseManagerKioskFlow(userData) ||
      shouldShowManagerAttendanceOrganizations(userData) ||
      isAdminAttendanceUser(userData);
    const shouldFilterManagerOrgs =
      shouldShowManagerAttendanceOrganizations(userData);

    const loadOrganizations = async () => {
      if (!clientId) {
        if (isMounted) {
          setOrganizations(scopedOrgs);
          setLoading(false);
        }
        return;
      }

      if (shouldFetchAllClientOrgs) {
        setLoading(true);
        try {
          const data = await fetchOrganizations(clientId);
          if (isMounted) {
            const allOrganizations = mergeOrganizationAttendanceSettings(
              userData,
              data?.organizations || []
            );
            setOrganizations(
              shouldFilterManagerOrgs
                ? allOrganizations.filter(isManagerAttendanceOrganization)
                : allOrganizations
            );
          }
        } catch {
          if (isMounted) {
            setOrganizations(scopedOrgs);
            toast.error("Failed to fetch organizations");
          }
        } finally {
          if (isMounted) setLoading(false);
        }
        return;
      }

      if (scopedOrgs.length) {
        if (isMounted) {
          setOrganizations(scopedOrgs);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const data = await fetchOrganizations(clientId);
        if (isMounted) {
          setOrganizations(data?.organizations || []);
        }
      } catch {
        if (isMounted) toast.error("Failed to fetch organizations");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadOrganizations();

    return () => {
      isMounted = false;
    };
  }, [userData]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f4f6f8",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<LogOutIcon size={16} />}
          onClick={handleLogout}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 1.75,
            py: 0.75,
            color: "#374151",
            borderColor: "#d1d5db",
            bgcolor: "#ffffff",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#f9fafb",
              borderColor: "#9ca3af",
              boxShadow: "none",
            },
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          Logout
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          px: { xs: 2, sm: 3 },
          pt: { xs: 1, sm: 1.5 },
          pb: { xs: 3, sm: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 920 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 2.5, sm: 3 } }}>
            <Typography
              sx={{
                fontSize: { xs: "1.45rem", sm: "1.75rem" },
                fontWeight: 700,
                color: "#111827",
                letterSpacing: "-0.02em",
                mb: 1,
              }}
            >
              Select your organization
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.9rem", sm: "1rem" },
                color: "#6b7280",
                maxWidth: 480,
                mx: "auto",
                lineHeight: 1.55,
              }}
            >
              Choose where you want to mark attendance. You will go to the face
              scan screen for that organization.
            </Typography>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid #e8ecf0",
                    bgcolor: "#ffffff",
                    p: 2.25,
                  }}
                >
                  <Skeleton variant="circular" width={48} height={48} />
                  <Skeleton variant="text" sx={{ mt: 2, width: "75%" }} />
                  <Skeleton variant="text" sx={{ width: "45%" }} />
                </Paper>
              ))}
            </Box>
          ) : organizations?.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                textAlign: "center",
                borderRadius: "16px",
                border: "1px solid #e8ecf0",
                bgcolor: "#ffffff",
              }}
            >
              <Building2 size={36} color="#9ca3af" style={{ marginBottom: 12 }} />
              <Typography
                sx={{
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                No organizations found
              </Typography>
              <Typography sx={{ mt: 0.75, color: "#6b7280", fontSize: "0.9rem" }}>
                Contact your administrator if you believe this is a mistake.
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              {organizations?.map((item) => {
                const isSwitching =
                  Number(switchingOrgId) === Number(item?.organization_id);
                const shortName =
                  item?.organization_short_name ||
                  `Org ${item?.organization_id}`;

                return (
                  <Paper
                    key={item?.organization_id}
                    elevation={0}
                    onClick={() =>
                      !isSwitching && handleOrganizationClick(item)
                    }
                    sx={{
                      cursor: isSwitching ? "wait" : "pointer",
                      borderRadius: "16px",
                      border: "1px solid #e8ecf0",
                      bgcolor: "#ffffff",
                      p: 2.25,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.75,
                      opacity: isSwitching ? 0.72 : 1,
                      transition:
                        "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                      "&:hover": {
                        borderColor: isSwitching ? "#e8ecf0" : "#4caf50",
                        boxShadow: isSwitching
                          ? "none"
                          : "0 10px 24px rgba(17, 24, 39, 0.08)",
                        transform: isSwitching ? "none" : "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "14px",
                        bgcolor: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: "#374151",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {getOrganizationInitials(item?.organization_name)}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#111827",
                          fontSize: { xs: "0.95rem", sm: "1rem" },
                          lineHeight: 1.35,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item?.organization_name}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.5,
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          fontWeight: 500,
                        }}
                      >
                        {shortName}
                      </Typography>
                      {isSwitching && (
                        <Typography
                          sx={{
                            mt: 0.75,
                            fontSize: "0.75rem",
                            color: "#4caf50",
                            fontWeight: 600,
                          }}
                        >
                          Opening scanner...
                        </Typography>
                      )}
                    </Box>

                    <ChevronRight
                      size={18}
                      color={isSwitching ? "#d1d5db" : "#9ca3af"}
                      style={{ flexShrink: 0 }}
                    />
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationChangePage;
